import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { AgreementStatus } from '@/domain/agreements/agreement'
import { DeathData, formatDeathRelationship, formatDeathState } from '@/domain/deathData/deathData'
import { OfferingStatus, ServiceType } from '@/domain/offerings/offering'
import { Currency, PaymentMethod, PaymentStatus } from '@/domain/payments/payments'
import { useAgreement, useCreateMultiAgreement } from '@/hooks/api/useAgreements'
import { useOfferings } from '@/hooks/api/useOfferings'
import { useParcel } from '@/hooks/api/useParcels'
import { Paths } from '@/routes/paths'
import { convertToDigitStr } from '@/utils/helpers'
import { useNavigate } from '@tanstack/react-router'
import { CheckCircle2, Loader2, MapPin } from 'lucide-react'
import { useEffect, useState } from 'react'
import { DeathDataCollection } from './DeathDataCollection'

interface OccupyingParcelCmpProps {
  parcelId: string
  contractId: string
}

const MANDATORY_SERVICE_CODE = '3'
const EXCLUDED_SERVICE_CODES = ['14', '8', '6']

export const OccupyingParcelCmp = ({ parcelId, contractId }: OccupyingParcelCmpProps) => {
  const navigate = useNavigate()
  const { data: parcel, isLoading: isLoadingParcel } = useParcel(parcelId)
  const { data: mainAgreement, isLoading: isLoadingAgreement } = useAgreement(contractId)
  const { data: offerings, isLoading: isLoadingOfferings } = useOfferings({
    status: OfferingStatus.ACTIVE,
    isAdditional: true,
    serviceType: ServiceType.PARCELAS
  })
  const createMultiMutation = useCreateMultiAgreement()

  const [selectedOfferingIds, setSelectedOfferingIds] = useState<string[]>([])
  const [selectedOfferingsData, setSelectedOfferingsData] = useState<Array<{ id: string; code: string; name: string; description?: string; basePrice?: number; serviceType?: ServiceType }>>([])
  const [isProcessing, setIsProcessing] = useState(false)
  const [step, setStep] = useState<'services' | 'deathData' | 'confirmation'>('services')
  const [deathData, setDeathData] = useState<DeathData[]>([])

  // console.log(deathData)

  useEffect(() => {
    if (offerings) {
      const mandatory = offerings.find(o => o.code === MANDATORY_SERVICE_CODE)
      if (mandatory) {
        setSelectedOfferingIds(prev => prev.includes(mandatory.id) ? prev : [...prev, mandatory.id])
        setSelectedOfferingsData(prev => prev.some(o => o.id === mandatory.id) ? prev : [mandatory])
      }
    }
  }, [offerings])

  if (isLoadingParcel || isLoadingAgreement || isLoadingOfferings) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    )
  }

  if (!parcel || !mainAgreement || !offerings) {
    return <div>Datos no encontrados</div>
  }

  const handleToggleOffering = (offeringId: string, mandatory: boolean) => {
    if (mandatory) return
    const offering = offerings?.find(o => o.id === offeringId)
    setSelectedOfferingIds(prev =>
      prev.includes(offeringId)
        ? prev.filter(id => id !== offeringId)
        : [...prev, offeringId]
    )
    if (offering) {
      setSelectedOfferingsData(prev =>
        prev.some(o => o.id === offeringId)
          ? prev.filter(o => o.id !== offeringId)
          : [...prev, offering]
      )
    }
  }

  const handleDeathDataSubmit = (data: DeathData[]) => {
    setDeathData(data)
    setStep('confirmation')
  }

  const handleBackFromDeathData = () => {
    setStep('services')
  }

  const onSubmit = async () => {
    if (selectedOfferingIds.length === 0) return

    setIsProcessing(true)
    try {
      const agreementsToCreate = selectedOfferingIds.map(offeringId => {
        const offering = selectedOfferingsData.find(o => o.id === offeringId)
        return {
          userId: mainAgreement.userId,
          offeringId: offeringId,
          amount: offering?.basePrice || 0,
          startDate: new Date(),
          status: AgreementStatus.Pending,
          mainContractId: mainAgreement.id,
        }
      })

      const response = await createMultiMutation.mutateAsync({
        agreements: agreementsToCreate,
        parcel: {
          parcelId: parcelId
        },
        deathData: deathData.length > 0 ? deathData : undefined
      })
      
      if (response.billingPeriodId && response.payments.length > 0) {
        navigate({ to: `${Paths.Management.SubscriptionPeriodPayments}/${response.billingPeriodId}/${Paths.Management.Payments}/${response.payments[0].id}` })
      } else {
        if (response.payments.length > agreementsToCreate.length) {
          navigate({ to: `${Paths.Management.MultiPayments}/${response.payments[response.payments.length - 1].id}` })
        }
        else {
          navigate({ to: `${Paths.Management.Contracts}/${mainAgreement.id}/${Paths.Management.Payments}/${response.payments[0].id}` })
        }
      }
    } catch (error) {
      console.error(error)
    } finally {
      setIsProcessing(false)
    }
  }

  const handleBackFromConfirmation = () => {
    setStep('deathData')
  }

  const mandatoryOffering = offerings?.find(o => o.code === MANDATORY_SERVICE_CODE)

  const totalAmount = selectedOfferingIds.reduce((sum, id) => {
    const off = selectedOfferingsData.find(o => o.id === id)
    return sum + (off?.basePrice || 0)
  }, 0)

  return (
    <div className="max-w-4xl mx-auto space-y-6 pb-12">
      <div className="space-y-1">
        <h1 className="text-3xl font-bold tracking-tight">Ocupar Parcela</h1>
        <p className="text-muted-foreground">
          {step === 'services' && (
            <>Seleccione los servicios adicionales para la inhumación en la parcela <span className="font-bold text-foreground">#{parcel.parcela}</span></>
          )}
          {step === 'deathData' && (
            <>Ingrese los datos de los fallecidos para la ocupación en la parcela <span className="font-bold text-foreground">#{parcel.parcela}</span></>
          )}
          {step === 'confirmation' && (
            <>Confirme la operación para el proceso de ocupación de la parcela <span className="font-bold text-foreground">#{parcel.parcela}</span></>
          )}
        </p>
      </div>

      {/* Step: Services Selection */}
      {step === 'services' && (
        <div className="space-y-6">
          <div className="grid gap-6 md:grid-cols-3">
            <Card className="md:col-span-2 space-y-4">
              <CardHeader className="pb-3 border-b">
                <CardTitle className="text-lg flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-blue-600" />
                  Servicios Disponibles
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-4 space-y-4">
                {offerings.filter(o => !EXCLUDED_SERVICE_CODES.includes(o.code)).map((offering) => {
                  const isMandatory = offering.code === MANDATORY_SERVICE_CODE
                  const isSelected = selectedOfferingIds.includes(offering.id) || isMandatory
                  return (
                    <div
                      key={offering.id}
                      className={`flex items-center justify-between p-4 rounded-xl border-2 transition-all ${isSelected ? 'border-blue-500 bg-blue-50/50' : 'border-gray-100 hover:border-gray-200 cursor-pointer'
                        }`}
                      onClick={() => handleToggleOffering(offering.id, isMandatory)}
                    >
                      <div className="flex items-center gap-4">
                        <Checkbox
                          checked={isSelected}
                          disabled={isMandatory}
                          onCheckedChange={() => handleToggleOffering(offering.id, isMandatory)}
                        />
                        <div className='flex items-center gap-1'>
                          <p className="font-bold">
                            <span className='font-mono'>
                              {offering.code}</span> - <span>{offering.name}</span>
                            {isMandatory && <span className="ml-2 text-[10px] bg-blue-100 text-blue-700 font-bold px-1.5 py-0.5 rounded uppercase inline-block">
                              Obligatorio
                            </span>}
                          </p>
                          <p className="text-xs text-muted-foreground">{offering.description}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-mono font-bold">${convertToDigitStr(offering.basePrice)}</p>
                      </div>
                    </div>
                  )
                })}
              </CardContent>
            </Card>

            <div className="space-y-6">
              <Card className="border-blue-100 bg-blue-50/30">
                <CardHeader className="pb-3 border-b border-blue-100">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <MapPin className="h-5 w-5 text-blue-600" />
                    Ubicación
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-4">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground uppercase text-[10px] font-bold">Sección</p>
                      <p className="font-medium text-lg">{parcel.section}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground uppercase text-[10px] font-bold">Fila</p>
                      <p className="font-medium text-lg">{parcel.fila}</p>
                    </div>
                    <div className="col-span-2">
                      <p className="text-muted-foreground uppercase text-[10px] font-bold">Parcela</p>
                      <p className="font-medium text-lg">#{parcel.parcela}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-gray-200 shadow-lg">
                <CardHeader className="pb-3 border-b">
                  <CardTitle className="text-lg">Resumen de Cargos</CardTitle>
                </CardHeader>
                <CardContent className="pt-4">
                  <div className="space-y-2">
                    {selectedOfferingIds.map(id => {
                      const off = selectedOfferingsData.find(o => o.id === id)
                      return (
                        <div key={id} className="flex justify-between text-sm">
                          <span className="text-muted-foreground line-clamp-1 mr-2">{off?.name}</span>
                          <span className="font-mono">${convertToDigitStr(off?.basePrice || 0)}</span>
                        </div>
                      )
                    })}
                  </div>
                  <div className="pt-4 border-t border-gray-100 flex justify-between items-center mt-4">
                    <span className="font-bold">Total a Cobrar</span>
                    <span className="text-xl font-bold text-blue-700 font-mono">${convertToDigitStr(totalAmount)}</span>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Buttons moved below both cards */}
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => window.history.back()}
              disabled={isProcessing}
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button
              variant="info"
              onClick={() => setStep('deathData')}
              disabled={isProcessing || selectedOfferingIds.length === 0}
              className="flex-1"
            >
              {isProcessing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : 'Siguiente'}
            </Button>
          </div>
        </div>
      )}

      {/* Step: Death Data */}
      {step === 'deathData' && mandatoryOffering && (
        <DeathDataCollection
          offering={mandatoryOffering}
          onSubmit={handleDeathDataSubmit}
          onBack={handleBackFromDeathData}
          peopleList={deathData}
          onPeopleListChange={setDeathData}
        />
      )}

      {/* Step: Confirmation */}
      {step === 'confirmation' && (
        <div className="space-y-6">
          <div className="grid gap-6 md:grid-cols-3">
            <Card className="md:col-span-2 space-y-4">
              <CardHeader className="pb-3 border-b">
                <CardTitle className="text-lg flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-blue-600" />
                  Confirmar y Generar Cargos
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-4 space-y-4">
                <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex gap-3">
                  <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0" />
                  <div className="text-sm text-green-800 space-y-3">
                    <p className="font-bold mb-1">Datos del Fallecido</p>
                    <div className="space-y-2">
                      {deathData.map((person, index) => {
                        const personLabel = `${index + 1}. ${person.name}`
                        return (
                          <div key={person.id || index} className="border border-green-200 rounded-lg p-3 bg-white/50">
                            <p className="font-bold text-green-900">{personLabel}</p>
                            <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-2 text-xs">
                              {person.docType && (
                                <div>
                                  <span className="font-medium text-green-700">Tipo Doc:</span>
                                  <span className="ml-1">{person.docType}</span>
                                </div>
                              )}
                              {person.docNumber && (
                                <div>
                                  <span className="font-medium text-green-700">Nro Doc:</span>
                                  <span className="ml-1">{person.docNumber}</span>
                                </div>
                              )}
                              {person.birthDate && (
                                <div>
                                  <span className="font-medium text-green-700">Fecha Nac.:</span>
                                  <span className="ml-1">{new Date(person.birthDate).toLocaleDateString('es-AR')}</span>
                                </div>
                              )}
                              {person.deathDate && (
                                <div>
                                  <span className="font-medium text-green-700">Fecha Fallecimiento:</span>
                                  <span className="ml-1">{new Date(person.deathDate).toLocaleDateString('es-AR')}</span>
                                </div>
                              )}
                              {person.state && (
                                <div>
                                  <span className="font-medium text-green-700">Estado:</span>
                                  <span className="ml-1">{formatDeathState(person.state)}</span>
                                </div>
                              )}
                              {person.cremationDate && (
                                <div>
                                  <span className="font-medium text-green-700">Fecha Cremación:</span>
                                  <span className="ml-1">{new Date(person.cremationDate).toLocaleDateString('es-AR')}</span>
                                </div>
                              )}
                              {person.intermentDate && (
                                <div>
                                  <span className="font-medium text-green-700">Fecha inhumación:</span>
                                  <span className="ml-1">{new Date(person.intermentDate).toLocaleDateString('es-AR')}</span>
                                </div>
                              )}
                              {person.reductionDate && (
                                <div>
                                  <span className="font-medium text-green-700">Fecha Reducción:</span>
                                  <span className="ml-1">{new Date(person.reductionDate).toLocaleDateString('es-AR')}</span>
                                </div>
                              )}
                              {person.thumbLevel && (
                                <div>
                                  <span className="font-medium text-green-700">Nivel en parcela:</span>
                                  <span className="ml-1">{person.thumbLevel}</span>
                                </div>
                              )}
                              {person.relationshipWithDeceased && (
                                <div>
                                  <span className="font-medium text-green-700">Parentesco:</span>
                                  <span className="ml-1">{formatDeathRelationship(person.relationshipWithDeceased)}</span>
                                </div>
                              )}
                              <div className="col-span-2 mt-1"></div>
                              {person.entryNumber && (
                                <div>
                                  <span className="font-medium text-green-700">Nro Acta:</span>
                                  <span className="ml-1">{person.entryNumber}</span>
                                </div>
                              )}
                              {person.volume && (
                                <div>
                                  <span className="font-medium text-green-700">Tomo:</span>
                                  <span className="ml-1">{person.volume}</span>
                                </div>
                              )}
                              {person.year && (
                                <div>
                                  <span className="font-medium text-green-700">Año:</span>
                                  <span className="ml-1">{person.year}</span>
                                </div>
                              )}
                              {person.leaf && (
                                <div>
                                  <span className="font-medium text-green-700">Folio:</span>
                                  <span className="ml-1">{person.leaf}</span>
                                </div>
                              )}
                              {person.office && (
                                <div>
                                  <span className="font-medium text-green-700">Oficina:</span>
                                  <span className="ml-1">{person.office}</span>
                                </div>
                              )}
                              {person.mortuary && (
                                <div>
                                  <span className="font-medium text-green-700">Funeraria:</span>
                                  <span className="ml-1">{person.mortuary}</span>
                                </div>
                              )}
                              {person.medicOfDeceased && (
                                <div>
                                  <span className="font-medium text-green-700">Médico:</span>
                                  <span className="ml-1">{person.medicOfDeceased}</span>
                                </div>
                              )}
                              {person.deathCertificateAuthorization && (
                                <div>
                                  <span className="font-medium text-green-700">Autorización de sepultura:</span>
                                  <span className="ml-1">{person.deathCertificateAuthorization}</span>
                                </div>
                              )}
                              {person.notes && (
                                <div className="col-span-2">
                                  <span className="font-medium text-green-700">Notas:</span>
                                  <span className="ml-1 block whitespace-break-spaces">{person.notes}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="space-y-6">
              <Card className="border-blue-100 bg-blue-50/30">
                <CardHeader className="pb-3 border-b border-blue-100">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <MapPin className="h-5 w-5 text-blue-600" />
                    Ubicación
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-4">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground uppercase text-[10px] font-bold">Sección</p>
                      <p className="font-medium text-lg">{parcel.section}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground uppercase text-[10px] font-bold">Fila</p>
                      <p className="font-medium text-lg">{parcel.fila}</p>
                    </div>
                    <div className="col-span-2">
                      <p className="text-muted-foreground uppercase text-[10px] font-bold">Parcela</p>
                      <p className="font-medium text-lg">#{parcel.parcela}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-gray-200 shadow-lg">
                <CardHeader className="pb-3 border-b">
                  <CardTitle className="text-lg">Resumen de Cargos</CardTitle>
                </CardHeader>
                <CardContent className="pt-4">
                  <div className="space-y-2">
                    {selectedOfferingIds.map(id => {
                      const off = selectedOfferingsData.find(o => o.id === id)
                      return (
                        <div key={id} className="flex justify-between text-sm">
                          <span className="text-muted-foreground line-clamp-1 mr-2">{off?.name}</span>
                          <span className="font-mono">${convertToDigitStr(off?.basePrice || 0)}</span>
                        </div>
                      )
                    })}
                  </div>
                  <div className="pt-4 border-t border-gray-100 flex justify-between items-center mt-4">
                    <span className="font-bold">Total a Cobrar</span>
                    <span className="text-xl font-bold text-blue-700 font-mono">${convertToDigitStr(totalAmount)}</span>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Buttons moved below both cards */}
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={handleBackFromConfirmation}
              disabled={isProcessing}
              className="flex-1"
            >
              Volver
            </Button>
            <Button
              variant="info"
              onClick={onSubmit}
              disabled={isProcessing || selectedOfferingIds.length === 0}
              className="flex-1"
            >
              {isProcessing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : 'Confirmar'}
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}